import * as Yup from "yup";
import { NMPClassification } from "./nmpClassificationTree.api";
// Step2-5 (Observations 1-4) are no longer driven by these static arrays —
// see NMPClassificationStep.tsx / getSchemeClassificationTree instead.
// Kept for NMPSchemaStep2-5 below, which are commented out but not deleted.
// import {
// 	step2Questions,
// 	step3Questions,
// 	step4Questions,
// 	step5Questions,
// } from "./NMPQuestions";

export const generateYupShape = (
	questions: { id: string }[],
	isAuditPara: boolean = false,
) => {
	const shape: any = {};
	questions.forEach((q) => {
		shape[q.id] = Yup.string().required("Observation Status is required");
		if (isAuditPara) {
			shape[`${q.id}_remarks`] = Yup.string().when(q.id, {
				is: "yes",
				then: (schema) => schema.required("Remarks is required"),
				otherwise: (schema) => schema.optional(),
			});
		} else {
			shape[`${q.id}_remarks`] = Yup.string().optional();
		}
	});
	return shape;
};

export const generateClassificationSchema = (
	classifications: NMPClassification[],
) => {
	const shape: any = {};
	classifications.forEach((classification) => {
		const isAuditPara =
			classification.name?.trim().toLowerCase() === "audit para";
		classification.categories.forEach((category) => {
			category.sub_categories.forEach((sub) => {
				const qId = `subcat_${sub.id}`;
				shape[qId] = Yup.string().required("Observation Status is required");
				if (isAuditPara) {
					shape[`${qId}_remarks`] = Yup.string().when(qId, {
						is: "yes",
						then: (schema) => schema.required("Remarks is required"),
						otherwise: (schema) => schema.optional(),
					});
				} else {
					shape[`${qId}_remarks`] = Yup.string().optional();
				}
			});
		});
	});
	return Yup.object().shape(shape);
};

export const generateInitialValues = (questions: { id: string }[]) => {
	const values: any = {};
	questions.forEach((q) => {
		values[q.id] = "";
		values[`${q.id}_remarks`] = "";
	});
	return values;
};

// Flattens a fetched classification tree into the {id: string}[] shape
// generateYupShape/generateInitialValues expect, using the same field-naming
// convention (subcat_<sub_category id>) as NMPClassificationStep.tsx.
export const flattenClassificationTree = (
	classifications: NMPClassification[],
): { id: string }[] => {
	const questions: { id: string }[] = [];
	classifications.forEach((classification) => {
		classification.categories.forEach((category) => {
			category.sub_categories.forEach((sub) => {
				questions.push({ id: `subcat_${sub.id}` });
			});
		});
	});
	return questions;
};

export const NMPSchemaStep1 = Yup.object().shape({
	district_id: Yup.number().required("District is required"),
	udise_code: Yup.string().required("UDISE Code is required"),
	school_name: Yup.string().required("School Name is required"),

	// Text, not a number: audit_calendars.round_no can hold non-numeric pilot
	// labels (e.g. "Pilot 2"), not just plain round numbers.
	round_no: Yup.string().required("Round No is required"),
	audit_start_date: Yup.string().required("Audit Start Date is required"),
	audit_end_date: Yup.string().required("Audit End Date is required"),
	sgs_date: Yup.string().required("Gram Sabha Date is required"),
	audit_year_id: Yup.number().required("Audit Year is required"),

	overall_quality_food: Yup.string().required("Food quality is required"),

	avg_current_year_children_prepared: Yup.number().required("Required").min(0),
	avg_current_year_children_consumed: Yup.number().required("Required").min(0),
	avg_current_year_eggs_consumed: Yup.number().required("Required").min(0),
	avg_current_year_rice_cooked: Yup.number().required("Required").min(0),

	avg_audit_period_children_prepared: Yup.number().required("Required").min(0),
	avg_audit_period_children_consumed: Yup.number().required("Required").min(0),
	avg_audit_period_eggs_consumed: Yup.number().required("Required").min(0),
	avg_audit_period_rice_cooked: Yup.number().required("Required").min(0),
});

// NMPSchemaStep2-5 superseded by the dynamic classification-tree schema built
// in NMPSchemeStepper.tsx (see nmp-classification-based-observations change).
// Commented out, not deleted, per this project's established convention.
// export const NMPSchemaStep2 = Yup.object().shape({
// 	...generateYupShape(step2Questions),
// });

// export const NMPSchemaStep3 = Yup.object().shape({
// 	...generateYupShape(step3Questions),
// });

// export const NMPSchemaStep4 = Yup.object().shape({
// 	...generateYupShape(step4Questions),
// });

// export const NMPSchemaStep5 = Yup.object().shape({
// 	...generateYupShape(step5Questions),
// });

export const NMPSchemaStep6 = Yup.object().shape({
	unique_aspects: Yup.string().required("Unique aspects is required"),
	committee_remarks: Yup.string().required("Committee remarks is required"),
	changes_due_to_audit: Yup.string().required("Required"),
	changes_due_to_audit_paragraph: Yup.string().when("changes_due_to_audit", {
		is: "yes",
		then: (schema) => schema.required("Please describe the changes"),
	}),
	photo_1: Yup.mixed().required("Photo 1 is compulsory"),
	report_file: Yup.mixed().required("Audit Report is required"),
});

export const initialNMPValues = {
	// Step 1
	district_id: "",
	udise_code: "",
	school_name: "",
	round_no: "",
	audit_start_date: "",
	audit_end_date: "",
	sgs_date: "",
	audit_year_id: "",
	overall_quality_food: "",

	avg_current_year_children_prepared: 0,
	avg_current_year_children_consumed: 0,
	avg_current_year_eggs_consumed: 0,
	avg_current_year_rice_cooked: 0,

	avg_audit_period_children_prepared: 0,
	avg_audit_period_children_consumed: 0,
	avg_audit_period_eggs_consumed: 0,
	avg_audit_period_rice_cooked: 0,

	drp_employee_code: "",
	drp_name: "",
	drp_mobile_no: "",
	drp_district_id: null,
	drp_block_id: null,
	vrp_details: [{ mobile_no: "", name: "", village_id: "" }],

	// Step 6 fields (Observations 5)
	unique_aspects: "",
	committee_remarks: "",
	changes_due_to_audit: "no",
	changes_due_to_audit_paragraph: "",
	case_study_1_type: "",
	case_study_1_file: null,
	case_study_2_type: "",
	case_study_2_file: null,
	photo_1: null,
	photo_2: null,
	photo_3: null,
	report_file: null,

	// Step2-5 (Observations 1-4) initial values are no longer static — see
	// flattenClassificationTree() + generateInitialValues(), merged in by
	// NMPSchemeStepper.tsx once the classification tree has loaded.
	// ...generateInitialValues(step2Questions),
	// ...generateInitialValues(step3Questions),
	// ...generateInitialValues(step4Questions),
	// ...generateInitialValues(step5Questions),
};
